import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ShortfallFixesState } from "@/generated/backend/v1/plan";
import { TrajectoryChart, TrajectoryLegend } from "@/charts/TrajectoryChart";
import { useAssistant } from "@/assistant/AssistantProvider";
import { ChartInfo } from "@/components/ChartInfo";
import { Copy } from "@/copy/copy";
import { addMonths, formatDate, formatMonth, formatWeekday } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import {
  debtComparison,
  flatActions,
  keptOver,
  isOnTrack,
  mostEfficientFix,
  netWorth,
  netWorthToday,
  netWorthSeries,
  paycheckSplit,
  priorityOutcomes,
  retirementMarker,
  shortfallView,
  spendingByCategory,
  isUnconfirmedFhsaTransfer,
  stateAt,
  groupRrspMatches,
  upcomingRecommendations,
} from "@/plan-logic/plan-view";
import { actionTitle, actionWhy, dueLabel } from "@/plan-logic/plan-view/actionText";
import { PlanPage, type PlanCtx } from "../PlanPage";
import { DataChecks } from "../dataChecks";
import { GroupRrspCard } from "./GroupRrspCard";
import { accuracyNote, planAccuracy } from "../sharpen/accuracy";
import { fixTitle } from "@/plan-logic/plan-view/fixText";

const CHIPS = [
  "Where does my money go each month?",
  "Should I pay the card or invest?",
  "When can I stop working?",
  "What if I lose my job?",
];

/** "67% funded": a priority's completion on one plan, capped at 100% (v40). */
const funded = (c: number | undefined) => `${Math.round(Math.min(1, c ?? 0) * 100)}% funded`;

/**
 * How Abi's plan moves a priority against the current trajectory (v44's `priWin`): a share of the
 * goal itself ("33% more of it paid for", "Fully paid for"), never points or dates.
 */
export function fundingMove(abi: number | undefined, naive: number | undefined): string {
  const a = Math.min(1, abi ?? 0);
  const d = Math.round((a - Math.min(1, naive ?? 0)) * 100);
  if (d === 0) return "No change";
  if (a >= 1 && d > 0) return "Fully paid for";
  return `${Math.abs(d)}% ${d > 0 ? "more" : "less"} of it paid for`;
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

/** The "Your plan runs short" banner (the prototype's `#short-banner`). */
export function ShortBanner({ ctx }: { ctx: PlanCtx }) {
  const navigate = useNavigate();
  const view = shortfallView(ctx.abi);
  if (!view) return null;
  const fix =
    ctx.stored.shortfallFixesState === ShortfallFixesState.READY
      ? mostEfficientFix(ctx.stored.shortfallFixes)
      : undefined;
  const years = Math.round(view.monthsAway / 12);
  const span =
    view.monthsAway >= 12
      ? `${years} year${years === 1 ? "" : "s"}`
      : `${view.monthsAway} month${view.monthsAway === 1 ? "" : "s"}`;
  // An out-of-date plan counts from its own start, not from today, and says so.
  const away = ctx.stale
    ? `It was ${span} away when this plan was made, in ${formatMonth(ctx.planStart)}`
    : `It is ${span} away`;
  return (
    <div className="shortban" data-testid="short-banner">
      <span className="bi">!</span>
      <div>
        <b>Your plan runs short in {formatMonth(view.date)}</b>
        <p>
          On the day it runs out, about {money(view.amount)} more is due than there is to pay it
          with. {away}
          {fix
            ? `, and the most efficient way to close it is ${fixTitle(fix).replace(/^./, (c) => c.toLowerCase())}.`
            : ctx.stored.shortfallFixesState === ShortfallFixesState.PENDING
              ? ". Abi is working out what closes it."
              : "."}
        </p>
      </div>
      <button type="button" className="go" onClick={() => navigate("/app/actions")}>
        See what closes it
      </button>
    </div>
  );
}

function HomeBody({ ctx }: { ctx: PlanCtx }) {
  const navigate = useNavigate();
  const assistant = useAssistant();
  const [ask, setAsk] = useState("");
  const { abi, naive, family, index, today } = ctx;

  const data = useMemo(() => {
    const all = flatActions(abi);
    // the one figure for today's net worth, as Net Worth and Achievements quote it (v45)
    const nw = netWorthToday(abi);
    const next = stateAt(abi, addMonths(ctx.planStart, 1));
    const split = paycheckSplit(spendingByCategory(abi, 12, undefined, all));
    // Money in, money out, and what's kept: one measure, Cash Flow's headline and Abi's impact's
    // (money in less money out; saving isn't money out). The bar below splits the same month.
    const flow = keptOver(abi);
    const perMonth = (x: number) => (flow.months ? x / flow.months : 0);
    const cash = { in: perMonth(flow.moneyIn), out: perMonth(flow.moneyOut), kept: flow.monthly };
    const debt = debtComparison(abi);

    const outcomes = priorityOutcomes(family, abi, naive);
    const recs = upcomingRecommendations(abi, { months: 2, limit: 4 });
    const matches = groupRrspMatches(abi, family, today);
    return { nw, nextNw: netWorth(next), split, cash, debt, outcomes, recs, matches };
  }, [abi, naive, family, ctx.planStart, today]);

  const acc = planAccuracy(family);
  const self = family.persons[0];
  const top = [...data.outcomes].sort((a, b) => b.importance - a.importance)[0];
  const onTrack = data.outcomes.filter(isOnTrack).length;
  const short = shortfallView(abi);
  const interest = Math.min(data.split.debt, data.debt.monthlyInterest);
  const segs: [string, number, string][] = [
    ["Essentials", data.split.essentials, "var(--ess)"],
    ["Lifestyle", data.split.lifestyle, "var(--life)"],
    ["Debt", data.split.debt - interest, "var(--debt)"],
    ["Interest", interest, "var(--warn)"],
    ["Priorities", data.split.priorities, "var(--gold)"],
    ["Kept", data.cash.kept, "var(--save)"],
  ];
  const openChat = (q?: string) => assistant?.open(q);

  const firstName = self?.givenName || "there";
  // An out-of-date plan's next steps are from its start, not this week (its banner says so).
  const thisWeek = ctx.stale
    ? 0
    : data.recs.filter(
        (r) =>
          r.first.date.year === today.year &&
          r.first.date.month === today.month &&
          (!r.first.date.day || r.first.date.day - today.day <= 7),
      ).length;

  return (
    <>
      <div style={{ maxWidth: 860, margin: "4px auto 30px" }}>
        <Copy
          k="pages.home.title"
          params={{ greeting: greeting(), name: firstName }}
          as="h1"
          id="greet"
          style={{ fontSize: 26, fontWeight: 600, textAlign: "center", marginBottom: 16 }}
        />
        <form
          className="card"
          style={{ padding: "15px 19px", display: "flex", alignItems: "center", gap: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            openChat(ask.trim() || "Can I afford to take three months off next year?");
            setAsk("");
          }}
        >
          <span className="mk" style={{ width: 22, height: 22, flex: "none" }} />
          <input
            id="ask"
            value={ask}
            onChange={(e) => setAsk(e.target.value)}
            aria-label="Ask Abi"
            style={{
              flex: 1,
              font: "inherit",
              fontSize: 16,
              border: 0,
              background: "none",
              color: "var(--ink)",
              outline: "none",
              minWidth: 0,
            }}
            placeholder="Can I afford to take three months off next year?"
            data-testid="ask-input"
          />
          <button className="btn" type="submit" data-testid="ask-submit">
            Ask
          </button>
        </form>
        <div
          style={{
            display: "flex",
            gap: 9,
            justifyContent: "center",
            marginTop: 14,
            flexWrap: "wrap",
          }}
        >
          {CHIPS.map((q) => (
            <button type="button" className="chip" key={q} onClick={() => openChat(q)}>
              {q}
            </button>
          ))}
        </div>
      </div>

      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 26, marginBottom: 24 }}>
        <p className="eyebrow" style={{ marginBottom: 10 }} id="today">
          {formatWeekday(today)}
        </p>
        <h2
          style={{
            fontSize: 27,
            lineHeight: 1.3,
            fontWeight: 600,
            maxWidth: "42ch",
            marginBottom: 7,
          }}
          data-testid="home-headline"
        >
          {short ? (
            <>
              Your plan <span style={{ color: "var(--warn)" }}>runs short</span> in{" "}
              {formatMonth(short.date)}.
            </>
          ) : onTrack === data.outcomes.length && data.outcomes.length > 0 ? (
            <>
              You&rsquo;re <span style={{ color: "var(--ok)" }}>on track</span>.
            </>
          ) : (
            <>
              {onTrack} of {data.outcomes.length} priorities are{" "}
              <span style={{ color: "var(--ok)" }}>on track</span>.
            </>
          )}{" "}
          {thisWeek
            ? `${thisWeek} thing${thisWeek === 1 ? " is" : "s are"} on your plan this week.`
            : data.recs.length && !ctx.stale
              ? `Next on your plan: ${actionTitle(data.recs[0].first, index).replace(/^./, (c) => c.toLowerCase())}.`
              : ""}
        </h2>
      </div>

      <div className="grid2">
        <button type="button" className="card pad panel" onClick={() => navigate("/app/net-worth")}>
          <div className="eyebrow">
            Household net worth<span className="goto">Open net worth &rarr;</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 14, margin: "8px 0 2px" }}>
            <div className="big tnum" data-testid="home-networth">
              {money(data.nw)}
            </div>
            {data.nextNw !== data.nw && (
              <div
                style={{
                  color: data.nextNw >= data.nw ? "var(--ok)" : "var(--warn)",
                  fontSize: 14,
                  fontWeight: 600,
                  paddingBottom: 6,
                }}
              >
                {data.nextNw >= data.nw ? "▲" : "▼"} {money(Math.abs(data.nextNw - data.nw))}{" "}
                {ctx.stale ? `by ${formatMonth(addMonths(ctx.planStart, 1))}` : "next month"}, on
                the plan
              </div>
            )}
          </div>
          <div className="mut" data-testid="home-networth-asof">
            At the end of {formatDate(ctx.planStart)}, the plan&rsquo;s first day
          </div>
          <div id="nwhome" className="gwrap" style={{ marginTop: 10 }}>
            <ChartInfo k="nwhome" />
            <TrajectoryChart
              abi={netWorthSeries(abi)}
              naive={naive ? netWorthSeries(naive) : undefined}
              height={210}
              retirement={retirementMarker(abi, family)}
            />
          </div>
          <TrajectoryLegend naive={!!naive} />
        </button>
        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 16 }}>
          <button
            type="button"
            className="card pad panel"
            onClick={() => navigate("/app/cash-flow")}
            style={{ display: "flex", flexDirection: "row", gap: 16, alignItems: "center" }}
          >
            <div style={{ flex: 1 }}>
              <div className="eyebrow">Money in</div>
              <div style={{ fontSize: 23, fontWeight: 700, marginTop: 6 }} className="tnum">
                {money(data.cash.in)}
              </div>
              <div className="mut">a month, after tax</div>
            </div>
            <div style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }} />
            <div style={{ flex: 1 }}>
              <div className="eyebrow">Money out</div>
              <div style={{ fontSize: 23, fontWeight: 700, marginTop: 6 }} className="tnum">
                {money(data.cash.out)}
              </div>
              <div className="mut">incl. {money(interest)} interest</div>
            </div>
            <div style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }} />
            <div style={{ flex: 1 }}>
              <div className="eyebrow">Kept</div>
              <div
                style={{
                  fontSize: 23,
                  fontWeight: 700,
                  marginTop: 6,
                  whiteSpace: "nowrap",
                  color: data.cash.kept >= 0 ? "var(--ok)" : "var(--warn)",
                }}
                className="tnum"
                data-testid="home-kept"
              >
                {money(data.cash.kept)}
              </div>
              <div className="mut">a month, money in less money out</div>
            </div>
            <span className="goto abs">Open cash flow &rarr;</span>
          </button>
          <button
            type="button"
            className="card pad panel"
            onClick={() => navigate("/app/cash-flow")}
            style={{ justifyContent: "center" }}
          >
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: 11 }}>
              <div className="eyebrow">Next 12 months, on average</div>
              <span className="mut" style={{ marginLeft: "auto" }} data-testid="home-kept-bar">
                {money(data.cash.kept)} kept
              </span>
            </div>
            <div
              style={{ display: "flex", height: 32, borderRadius: 7, overflow: "hidden", gap: 2 }}
              role="img"
              aria-label={segs.map(([n, v]) => `${n} ${money(v)}`).join(", ")}
            >
              {segs
                .filter(([, v]) => v > 0.5)
                .map(([n, v, c]) => (
                  <div key={n} style={{ flex: v, background: c }} />
                ))}
            </div>
            <div className="legend" style={{ marginTop: 12, fontSize: 11.5 }}>
              {segs.map(([n, , c]) => (
                <span key={n}>
                  <i className="lgd" style={{ background: c }} />
                  {n}
                </span>
              ))}
            </div>
          </button>
        </div>
      </div>

      <div className="grid2 mt">
        <div className="card pad">
          <div className="acthead">
            <h2>Implement your plan</h2>
            <div className="accwrap">
              <div className="accrow">
                <span className="eyebrow">Plan accuracy</span>
                <b id="acc-num" className="tnum" data-testid="home-accuracy">
                  {acc.accuracy}%
                </b>
              </div>
              <div className="acctrack">
                <span id="acc-fill" style={{ width: `${acc.accuracy}%` }} />
              </div>
              <span className="mut" id="acc-note">
                {accuracyNote(acc.left)}
              </span>
            </div>
            <Link className="shbtn" to="/app/sharpen" data-testid="home-sharpen">
              Sharpen your plan &rarr;
            </Link>
          </div>
          <div id="act-list" data-testid="home-actions">
            <DataChecks family={family} plan={abi} index={index} />
            {data.recs.length === 0 && (
              <p className="mut">Nothing to do right now beyond your usual bills.</p>
            )}
            {data.recs.map((r) => {
              const f = r.first;
              const amount = r.occurrences > 1 ? r.total / r.occurrences : f.amount;
              const row = (
                <>
                  <span style={{ flex: 1 }}>
                    <b>
                      {actionTitle(f, index, amount)}
                      {r.occurrences > 1 ? `, ${r.occurrences} times` : ""}
                    </b>
                    {actionWhy(f, index) && (
                      <span className={`wy${isUnconfirmedFhsaTransfer(f.action) ? " warn" : ""}`}>
                        {actionWhy(f, index)}
                      </span>
                    )}
                  </span>
                  <span className="wt">{dueLabel(f.date, today)}</span>
                </>
              );
              const key = `${f.kind}:${f.targetUuid}:${f.action.id}`;
              const t = f.action.actionType;
              // recorded once it's changed at work, so it links to the field
              return t.oneofKind === "setGroupRrspContributionAction" ? (
                <Link
                  className="actrow flat"
                  key={key}
                  to={`/app/update-data?job=${t.setGroupRrspContributionAction.jobUuid}&rate=${t.setGroupRrspContributionAction.employeeContributionRate}`}
                  data-testid="home-action-contribution"
                >
                  {row}
                </Link>
              ) : (
                <div className="actrow flat" key={key}>
                  {row}
                </div>
              );
            })}
          </div>
        </div>

        {top && (
          <button
            type="button"
            className="card pad panel"
            onClick={() => navigate("/app/priorities")}
          >
            <div className="eyebrow">
              Your top priority<span className="goto">Open priorities &rarr;</span>
            </div>
            <div style={{ fontSize: 21, fontWeight: 600, margin: "9px 0 2px" }}>{top.name}</div>
            <p className="mut" style={{ marginBottom: 18 }}>
              {top.isRetirement
                ? `Measured against the nest egg your plan needs${top.abi?.lastDue ? ` by ${top.abi.lastDue.year}` : ""}.`
                : `${top.importance} out of 10${top.abi?.firstDue ? `, due ${formatDate(top.abi.firstDue)}` : ""}.`}
            </p>
            {naive && (
              <div className="ptrow">
                <span className="plab">Your current trajectory</span>
                <span className="ptrack">
                  <span
                    className="pfill"
                    style={{
                      width: `${Math.round(Math.min(1, top.naive?.completion ?? 0) * 100)}%`,
                      background: "var(--before)",
                    }}
                  />
                </span>
                <span className="pval tnum">
                  {Math.round(Math.min(1, top.naive?.completion ?? 0) * 100)}%
                </span>
              </div>
            )}
            <div className="ptrow">
              <span className="plab">Abi’s plan</span>
              <span className="ptrack">
                <span
                  className="pfill"
                  style={{
                    width: `${Math.round(Math.min(1, top.abi?.completion ?? 0) * 100)}%`,
                    background: "var(--after)",
                  }}
                />
              </span>
              <span className="pval tnum">
                {Math.round(Math.min(1, top.abi?.completion ?? 0) * 100)}%
              </span>
            </div>
            <div className="pfoot">
              {naive && (
                <div>
                  <span className="eyebrow">Current trajectory</span>
                  <b style={{ color: "var(--before)" }}>{funded(top.naive?.completion)}</b>
                </div>
              )}
              <div>
                <span className="eyebrow">Abi’s plan</span>
                <b style={{ color: "var(--after)" }}>{funded(top.abi?.completion)}</b>
              </div>
              {naive && (
                <span className="pill">
                  {fundingMove(top.abi?.completion, top.naive?.completion)}
                </span>
              )}
            </div>
          </button>
        )}
      </div>
      {data.matches.length > 0 && <GroupRrspCard matches={data.matches} stopsAt={short?.date} />}
    </>
  );
}

export function HomePage() {
  return (
    <PlanPage testId="home" bare retiredBanner="fix" banners={(ctx) => <ShortBanner ctx={ctx} />}>
      {(ctx) => <HomeBody ctx={ctx} />}
    </PlanPage>
  );
}
